import { useEffect, useMemo, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import LeadFormDialog from "@/components/dialogs/LeadFormDialog";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Phone, Mail, IndianRupee, CalendarClock, Target, CheckCircle2 } from "lucide-react";

const STAGES = ["Lead", "Qualified", "Proposal", "Won", "Lost"];
const STAGE_STYLES = {
  Lead: "border-t-[#4B5563]",
  Qualified: "border-t-[#0EA5E9]",
  Proposal: "border-t-amber-500",
  Won: "border-t-[#00823B]",
  Lost: "border-t-red-500",
};

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editingLead, setEditingLead] = useState(null);

  const load = () => api.get("/leads").then((r) => setLeads(r.data)).catch(() => {});

  useEffect(() => {
    load();
    api.get("/employees/directory").then((r) => setEmployees(r.data)).catch(() => setEmployees([]));
  }, []);

  const employeesMap = useMemo(() => Object.fromEntries(employees.map((e) => [e.id, e.name])), [employees]);

  const grouped = useMemo(() => {
    const map = Object.fromEntries(STAGES.map((s) => [s, []]));
    leads.forEach((l) => map[l.stage]?.push(l));
    return map;
  }, [leads]);

  const totals = useMemo(() => {
    const pipelineValue = leads.filter((l) => !["Won", "Lost"].includes(l.stage)).reduce((s, l) => s + (l.expected_value || 0), 0);
    const wonValue = leads.filter((l) => l.stage === "Won").reduce((s, l) => s + (l.expected_value || 0), 0);
    return { pipelineValue, wonValue };
  }, [leads]);

  const changeStage = async (lead, stage) => {
    try {
      await api.put(`/leads/${lead.id}`, { stage });
      toast.success(stage === "Won" ? "Lead marked Won — Client record created" : `Lead moved to ${stage}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const deleteLead = async (id) => {
    try {
      await api.delete(`/leads/${id}`);
      toast.success("Lead removed");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const openAdd = () => {
    setEditingLead(null);
    setFormOpen(true);
  };

  const openEdit = (lead) => {
    setEditingLead(lead);
    setFormOpen(true);
  };

  const currency = (n) => `\u20B9${Number(n || 0).toLocaleString("en-IN")}`;

  return (
    <DashboardLayout title="Leads">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div className="flex items-center gap-5 text-sm">
          <span className="flex items-center gap-1.5 text-[#4B5563]"><Target className="h-4 w-4 text-[#0EA5E9]" /> Open Pipeline: <strong className="text-[#1C1C1E]">{currency(totals.pipelineValue)}</strong></span>
          <span className="flex items-center gap-1.5 text-[#4B5563]"><CheckCircle2 className="h-4 w-4 text-[#00823B]" /> Won: <strong className="text-[#1C1C1E]">{currency(totals.wonValue)}</strong></span>
        </div>
        <Button data-testid="add-lead-button" onClick={openAdd} className="bg-[#76C121] hover:bg-[#5C9819] text-white">
          <Plus className="h-4 w-4 mr-1.5" /> Add Lead
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-4" data-testid="leads-pipeline-board">
        {STAGES.map((stage) => (
          <div key={stage} className={`bg-white border border-[#E2E8F0] border-t-4 rounded-lg ${STAGE_STYLES[stage]}`} data-testid={`leads-column-${stage}`}>
            <div className="px-3.5 py-2.5 border-b border-[#E2E8F0] flex items-center justify-between">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-[#1C1C1E]">{stage}</h3>
              <span className="text-xs text-[#4B5563]">{grouped[stage].length}</span>
            </div>
            <div className="p-2.5 space-y-2.5 min-h-[120px] max-h-[60vh] overflow-y-auto">
              {grouped[stage].length === 0 && <p className="text-xs text-[#4B5563] text-center py-4">No leads.</p>}
              {grouped[stage].map((l) => (
                <div key={l.id} data-testid={`lead-card-${l.id}`} className="border border-[#E2E8F0] rounded-md p-3 hover:bg-[#F8F9FA] transition-colors duration-200">
                  <div className="flex items-start justify-between gap-2">
                    <p onClick={() => openEdit(l)} className="text-sm font-medium text-[#1C1C1E] cursor-pointer truncate">{l.company_name}</p>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button data-testid={`lead-delete-${l.id}`} className="text-red-600 shrink-0"><Trash2 className="h-3.5 w-3.5" /></button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete "{l.company_name}"?</AlertDialogTitle>
                          <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => deleteLead(l.id)} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                  {l.contact_name && <p className="text-xs text-[#4B5563] mt-0.5 truncate">{l.contact_name}</p>}
                  <div className="flex items-center gap-3 mt-1.5 flex-wrap text-xs text-[#4B5563]">
                    {l.expected_value > 0 && <span className="flex items-center gap-0.5"><IndianRupee className="h-3 w-3" />{Number(l.expected_value).toLocaleString("en-IN")}</span>}
                    {l.contact_email && <Mail className="h-3 w-3" />}
                    {l.contact_phone && <Phone className="h-3 w-3" />}
                  </div>
                  {l.next_follow_up_date && (
                    <p className="flex items-center gap-1 text-xs text-amber-700 mt-1">
                      <CalendarClock className="h-3 w-3" /> Follow-up: {l.next_follow_up_date}
                    </p>
                  )}
                  <p className="text-[10px] text-[#4B5563] mt-1.5">Owner: {employeesMap[l.owner_id] || "Unassigned"}</p>
                  <Select value={l.stage} onValueChange={(v) => changeStage(l, v)}>
                    <SelectTrigger data-testid={`lead-stage-select-${l.id}`} className="h-7 text-xs mt-2"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <LeadFormDialog open={formOpen} onOpenChange={setFormOpen} lead={editingLead} employees={employees} onSaved={load} />
    </DashboardLayout>
  );
}
